import React from 'react'
import clsx from 'clsx'
import { motion, AnimatePresence } from 'framer-motion'
import { Drawer as DrawerStyles } from '@airbus/elements/elements'
import { createUseStyles, ColorModeProvider } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import Modal, { ModalProps } from '../Modal/Modal'
import { SidesAnchor, ThemeMode } from '../helperTypes'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(DrawerStyles, 'drawer')

type DeprecatedProps = {
  color?: 'dark' | 'light' | 'primary' | 'secondary'
  duration?: number
  view?: boolean
}
interface Props extends ModalProps {
  /**
   * Side from which the drawer will appear
   * @default "left"
   */
  anchor?: SidesAnchor
  /** **Not implemented yet** */
  // swipeable?: boolean;
  /**
   * Theme/color that will impact children variants
   * @default "light"
   */
  theme?: ThemeMode
}

export type DrawerProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: DrawerProps) => {
  const { view, duration, color, ...rest } = props
  return {
    theme: color === 'primary' || color === 'secondary' ? 'dark' : color,
    open: view,
    ...rest,
  }
}

export const Drawer = (props: DrawerProps) => {
  const {
    anchor = 'left',
    className,
    children,
    onClose,
    open,
    style,
    theme = 'light',
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps(
    'Drawer',
    {
      view: 'open',
      color: 'theme',
      duration: undefined,
    },
    props,
  )
  const classes = useStyles()
  const horizontal = anchor === 'top' || anchor === 'bottom'
  const end = anchor === 'right' || anchor === 'bottom'
  const transitionProp = horizontal ? 'y' : 'x'
  const transitionVal = end ? '100%' : '-100%'
  return (
    <Modal open={open} onClose={onClose} closeAfterDelay={0.1} className={classes.modal} {...rest}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ [transitionProp]: transitionVal }}
            animate={{ [transitionProp]: 0 }}
            exit={{ [transitionProp]: transitionVal }}
            transition={{ type: 'tween', duration: 0.1 }}
            className={clsx(
              className,
              classes.root,
              classes[`_${anchor}`],
              classes[`_${theme}`],
              horizontal && classes._horizontal,
            )}
            style={style}
          >
            <ColorModeProvider mode={theme === 'light' ? 'default' : 'dark'}>{children}</ColorModeProvider>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  )
}

export default Drawer
